import { ArrowRight, TrendingUp } from "lucide-react";
import ScoreInfoTip from "@/components/ScoreInfoTip";

/**
 * ScoreImprovementPanel - the "Before vs After" hero panel that appears
 * once the AI Alignment rescore completes. Bundled here with its three
 * private sub-components (single row, category delta table, residual
 * gaps panel) because they only exist to serve this panel.
 */

function ScoreImprovementRow({ label, before, after, testid, infoVariant }) {
  const b = typeof before === "number" ? before : null;
  const a = typeof after === "number" ? after : null;
  if (b === null || a === null) return null;
  const delta = a - b;
  const isUp = delta > 0;
  const isDown = delta < 0;
  const deltaTone = isUp
    ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10"
    : isDown
      ? "text-rose-400 border-rose-500/30 bg-rose-500/10"
      : "text-zinc-400 border-zinc-700 bg-zinc-800/40";
  const deltaLabel = delta === 0
    ? "No change."
    : `${isUp ? "\u25B2" : "\u25BC"} ${isUp ? "+" : ""}${delta} percentage point${Math.abs(delta) === 1 ? "" : "s"}`;
  return (
    <div
      data-testid={testid}
      className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-emerald-500/15 bg-zinc-950/40 px-3.5 py-2.5"
    >
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
          {infoVariant && <ScoreInfoTip variant={infoVariant} label={label} />}
        </div>
        <div className="mt-1 flex items-baseline gap-2 text-sm">
          <span
            data-testid={`${testid}-before`}
            className="text-zinc-500 tabular-nums line-through decoration-zinc-700/70"
          >
            {b}%
          </span>
          <ArrowRight className="h-3 w-3 text-zinc-600" />
          <span
            data-testid={`${testid}-after`}
            className="text-emerald-300 font-semibold tabular-nums"
          >
            {a}%
          </span>
        </div>
      </div>
      <span
        data-testid={`${testid}-delta`}
        className={`inline-flex items-center gap-1 border rounded-full px-2.5 py-1 text-[11px] font-semibold whitespace-nowrap ${deltaTone}`}
      >
        {deltaLabel}
      </span>
    </div>
  );
}

const CATEGORY_LABELS = {
  skills_alignment: "Skills Alignment",
  experience_relevance: "Experience Relevance",
  keyword_alignment: "Keyword Alignment",
  achievement_relevance: "Achievement Relevance",
  ats_structure: "ATS Structure",
};

function CategoryDeltaTable({ before, after }) {
  const bC = before?.category_scores || {};
  const aC = after?.category_scores || {};
  const rows = Object.entries(CATEGORY_LABELS)
    .map(([key, label]) => ({ key, label, b: bC[key], a: aC[key] }))
    .filter((r) => typeof r.b === "number" && typeof r.a === "number");
  if (rows.length === 0) return null;
  const overallB = before?.match_score;
  const overallA = after?.match_score;
  if (typeof overallB === "number" && typeof overallA === "number") {
    rows.push({ key: "overall", label: "Overall Alignment", b: overallB, a: overallA, isOverall: true });
  }
  return (
    <div className="rounded-lg border border-emerald-500/15 bg-zinc-950/40 overflow-hidden" data-testid="category-delta-table">
      <div className="grid grid-cols-[1fr_auto_auto_auto] text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800/60 px-3.5 py-2 gap-3">
        <div>Category</div>
        <div className="text-right w-14">Before</div>
        <div className="text-right w-14">After</div>
        <div className="text-right w-16">Change</div>
      </div>
      {rows.map((r) => {
        const delta = r.a - r.b;
        const isUp = delta > 0, isDown = delta < 0;
        const tone = isUp ? "text-emerald-400" : isDown ? "text-rose-400" : "text-zinc-500";
        return (
          <div
            key={r.key}
            data-testid={`category-delta-${r.key}`}
            className={`grid grid-cols-[1fr_auto_auto_auto] items-center px-3.5 py-2 gap-3 text-xs border-t border-zinc-800/40 ${r.isOverall ? "bg-emerald-500/[0.04] font-semibold" : ""}`}
          >
            <div className={r.isOverall ? "text-emerald-200" : "text-zinc-200"}>{r.label}</div>
            <div className="text-right w-14 tabular-nums text-zinc-500 line-through decoration-zinc-700/70">{r.b}</div>
            <div className="text-right w-14 tabular-nums text-emerald-300">{r.a}</div>
            <div className={`text-right w-16 tabular-nums ${tone}`}>{isUp ? "+" : ""}{delta}</div>
          </div>
        );
      })}
    </div>
  );
}

function ResidualGapsPanel({ analysis }) {
  const req = (analysis?.required_gaps || []).filter(Boolean);
  const pref = (analysis?.preferred_gaps || []).filter(Boolean);
  if (req.length === 0 && pref.length === 0) return null;
  return (
    <div
      data-testid="residual-gaps-panel"
      className="rounded-lg border border-amber-500/25 bg-amber-500/[0.04] px-3.5 py-3 text-xs space-y-2"
    >
      <div className="text-[10px] uppercase tracking-widest text-amber-300 inline-flex items-center gap-1.5">
        Honest gaps still worth noting
      </div>
      {req.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-400/80 mb-1">Required qualifications not evidenced</div>
          <ul className="space-y-0.5">
            {req.slice(0, 6).map((g, i) => (
              <li key={i} className="text-amber-100/90"><span className="text-amber-400 mr-1.5">•</span>{g}</li>
            ))}
          </ul>
        </div>
      )}
      {pref.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-400/60 mb-1">Nice-to-haves you could still add if genuine</div>
          <ul className="space-y-0.5">
            {pref.slice(0, 4).map((g, i) => (
              <li key={i} className="text-amber-100/70"><span className="text-amber-400/70 mr-1.5">•</span>{g}</li>
            ))}
          </ul>
        </div>
      )}
      <p className="text-[11px] text-zinc-400 pt-1 border-t border-amber-500/15">
        Intuiterview never invents skills to boost your score. These are honest gaps to address with real experience or new learning.
      </p>
    </div>
  );
}

export default function ScoreImprovementPanel({ original, updated, updatedAnalysis }) {
  const b = original || {};
  const a = updated || {};
  const hasMatch = typeof b.match_score === "number" && typeof a.match_score === "number";
  const hasATS = typeof b.ats_score === "number" && typeof a.ats_score === "number";
  const hasKw = typeof b.keyword_match_score === "number" && typeof a.keyword_match_score === "number";
  if (!hasMatch && !hasATS && !hasKw) return null;
  const matchDelta = hasMatch ? a.match_score - b.match_score : 0;

  // Compute the biggest legitimate improvement across every tracked metric
  // (match, ATS, keyword + per-category scores). When the top-line match
  // score didn't move, we lead with whichever category DID improve - this
  // stops the "No change." bummer copy from being the hero moment.
  const CATEGORY_HERO_LABEL = {
    skills_alignment: "Skills Alignment",
    experience_relevance: "Experience Relevance",
    keyword_alignment: "Keyword Alignment",
    achievement_relevance: "Achievement Relevance",
    ats_structure: "ATS Structure",
  };
  const candidates = [];
  if (hasMatch)  candidates.push({ label: "Job Match",  before: b.match_score,        after: a.match_score });
  if (hasATS)    candidates.push({ label: "ATS Score",     before: b.ats_score,          after: a.ats_score });
  const bCat = b.category_scores || {}, aCat = a.category_scores || {};
  Object.entries(CATEGORY_HERO_LABEL).forEach(([key, label]) => {
    if (typeof bCat[key] === "number" && typeof aCat[key] === "number") {
      candidates.push({ label, before: bCat[key], after: aCat[key] });
    }
  });
  const bestGain = candidates.reduce((best, c) => {
    const d = c.after - c.before;
    return d > (best?.delta ?? -Infinity) ? { ...c, delta: d } : best;
  }, null);

  let summary;
  if (matchDelta > 0) {
    summary = `Your AI-optimized resume improved your match for this job by +${matchDelta} percentage point${matchDelta === 1 ? "" : "s"}.`;
  } else if (bestGain && bestGain.delta > 0) {
    summary = `Match score held steady, but ${bestGain.label} jumped from ${bestGain.before} to ${bestGain.after} (+${bestGain.delta}). The rewrite strengthened your evidence in the areas the JD emphasizes.`;
  } else if (matchDelta < 0) {
    summary = `Your AI-optimized resume shifted your match score by ${matchDelta} percentage point${Math.abs(matchDelta) === 1 ? "" : "s"}, review the change notes and re-run alignment if needed.`;
  } else {
    summary = "Your resume was already well-aligned to this role. The AI focused on structure and phrasing polish; try aligning against a different role to see a larger score lift.";
  }

  return (
    <div
      data-testid="score-improvement-panel"
      className="rounded-xl border border-emerald-500/25 bg-gradient-to-br from-emerald-500/[0.05] via-zinc-900/40 to-zinc-950/60 p-4 space-y-3"
    >
      <div className="flex items-center gap-2">
        <TrendingUp className="h-4 w-4 text-emerald-400" />
        <div className="text-[10px] uppercase tracking-widest text-emerald-400">
          Score improvement · Before vs After
        </div>
      </div>

      {/* Hero row, the single biggest improvement, so the panel always
          has a wow moment even when the match score itself held flat. */}
      {bestGain && bestGain.delta > 0 && (
        <div data-testid="score-improvement-hero" className="rounded-lg border border-emerald-500/30 bg-emerald-500/[0.08] px-4 py-3 flex items-center justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-300/80">Biggest win</div>
            <div className="mt-0.5 text-sm text-zinc-100">{bestGain.label}</div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-zinc-500 tabular-nums line-through decoration-zinc-700/70">{bestGain.before}</span>
            <ArrowRight className="h-3 w-3 text-zinc-600" />
            <span className="text-emerald-300 text-2xl font-bold tabular-nums">{bestGain.after}</span>
            <span className="text-emerald-400 font-semibold tabular-nums text-sm ml-1">+{bestGain.delta}</span>
          </div>
        </div>
      )}
      <div className="space-y-2">
        <ScoreImprovementRow
          label="Job Match Score"
          before={b.match_score}
          after={a.match_score}
          testid="score-improvement-match"
          infoVariant="match"
        />
        <ScoreImprovementRow
          label="ATS Score"
          before={b.ats_score}
          after={a.ats_score}
          testid="score-improvement-ats"
          infoVariant="ats"
        />
      </div>

      <CategoryDeltaTable before={b} after={a} />
      <ResidualGapsPanel analysis={updatedAnalysis} />
      <p
        data-testid="score-improvement-summary"
        className="text-xs text-zinc-300 leading-relaxed pt-1"
      >
        {summary}
      </p>
    </div>
  );
}
